<template>
  <div v-if="isUploadingAsset" :class="selectedAssetClassNames">
    <component
      :is="uploadStatusSpecific"
      :asset="asset"
      :is-narration="isNarration"
      @remove-asset="$emit('remove-asset', asset)"
    />
  </div>
  <div v-else :class="selectedAssetClassNames">
    <wt-progress-native
      v-if="isPreparing && !isPreparingFailed"
      :class="{ '-progress': true }"
      :value="preparingProgress"
      :max="100"
    />
    <component
      :is="assetTypeSpecific"
      class="-asset"
      :asset="asset"
      :show-actions="showActions"
      :media-co-assets="mediaCoAssets"
      :custom-overlay-mode="customOverlayMode"
      :overlay="overlay"
      :can-add="canAdd && !isSceneEdit && !isPreparing && !isPreparingFailed"
      @remove="ASSET_UNSELECTED(asset)"
      @locate="locateAssetOnTimeline"
      @edit="editAsset"
      @add="addAsset"
      @preview-asset="previewAsset"
    />
    <slot />
  </div>
</template>

<script>
import app from 'app';
import wtUploadAsset from './states/wtUploadAsset.vue';
import wtProcessingAsset from './states/wtProcessingAsset.vue';
import wtFailedAssetCompact from './states/wtFailedAssetCompact.vue';
import ImageAsset from './lightbox/wtImageAsset.component.vue';
import AudioAsset from './lightbox/wtAudioAsset.component.vue';
import WallpaperAsset from './lightbox/wtWallpaperAsset.component.vue';
import FragmentAsset from './lightbox/wtFragmentAsset.component.vue';
import SocialAsset from './lightbox/wtSocialAsset.component.vue';
import GifAsset from './lightbox/wtGifAsset.component.vue';
import MapAsset from './lightbox/wtMapAsset.component.vue';
import AssetMixins from './mixins/AssetMixins.vue';
import storyViewBus from 'v/Story/storyViewBus';
import { mapState, mapActions } from 'vuex';

export default {
  mixins: [AssetMixins],
  components: {
    wtUploadAsset,
    wtProcessingAsset,
    wtFailedAssetCompact,
    'wt-image-asset': ImageAsset,
    'wt-wallpaper-asset': WallpaperAsset,
    'wt-fragment-asset': FragmentAsset,
    'wt-gif-asset': GifAsset,
    'wt-social-asset': SocialAsset,
    'wt-audio-asset': AudioAsset,
    'wt-map-asset': MapAsset,
  },

  props: {
    asset: { type: Object, required: true },
    uploading: { type: Boolean, default: false },
    isNarration: { type: Boolean, default: false },
    overlay: { type: Boolean, default: false },
    mediaCoAssets: { type: Array, default: () => [] },
  },

  data: () => ({
    preparingProgress: 0,
  }),

  computed: {
    uploadStatus() {
      return this.asset.metadata.uploadStatus;
    },

    isUploadingAsset() {
      return (
        this.uploadStatus &&
        this.uploadStatus !== app.uploadStatus.DONE &&
        this.uploadStatusSpecific
      );
    },

    assetTypeSpecific() {
      if (app.assetType.isOnlyIMAGE(this.asset)) {
        return 'wt-image-asset';
      }
      if (app.assetType.isFRAGMENT(this.asset)) {
        return 'wt-fragment-asset';
      }
      if (app.assetType.isGIF(this.asset)) {
        return 'wt-gif-asset';
      }
      if (app.assetType.isSOCIAL(this.asset)) {
        return 'wt-social-asset';
      }
      if (app.assetType.isMAP(this.asset)) {
        return 'wt-map-asset';
      }
      if (app.assetType.isAUDIO(this.asset)) {
        return 'wt-audio-asset';
      }
      if (app.assetType.isWALLPAPER(this.asset)) {
        return 'wt-wallpaper-asset';
      }
      return 'wt-image-asset';
    },

    uploadStatusSpecific() {
      if (this.isPreparing || this.isPreparingFailed) {
        return null;
      }

      const status = this.asset.metadata.uploadStatus;
      const name = _.kebabCase(status);

      switch (status) {
        case 'UPLOAD':
          return `wt-${name}-asset`;
        case 'PROCESSING':
          return `wt-${name}-asset`;
        case 'FAILED':
          return `wt-${name}-asset-compact`;
        default:
          return null;
      }
    },

    selectedAssetClassNames() {
      return {
        'wt-lightbox-asset': true,
        '-preparing': this.isPreparing,
        '-preparing-failed': this.isPreparingFailed,
        '-expired': app.assetType.isExpired(this.asset),
      };
    },

    isSceneEdit() {
      return this.MODE_SCENE_EDIT;
    },
  },

  watch: {
    uploadStatus: {
      handler(newStatus) {
        if (this.uploading && newStatus === app.uploadStatus.DONE) {
          this.$emit('upload-done', this.asset);
        }
      },
      immediate: true,
    },
  },

  created() {
    if (this.isPreparing && this.preparingProgress === 0) {
      this.startPreparingProgress();
    }
  },

  methods: {
    ...mapActions(['ASSET_UNSELECTED']),

    locateAssetOnTimeline() {
      this.$emit('locate', this.asset);
    },

    startPreparingProgress() {
      if (this.isPreparing && this.preparingProgress < 99) {
        this.preparingProgress += 1;
        _.delay(this.startPreparingProgress.bind(this), 250);
      }
    },

    previewAsset() {
      if (this.isPreparing || this.isPreparingFailed) {
        return;
      }
      this.$emit('preview-asset', this.asset);
    },

    editAsset() {
      if (this.isPreparing || this.isPreparingFailed) {
        return;
      }
      this.$emit('edit', this.asset);
    },

    addAsset() {
      this.$emit('add', this.asset);
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'style_guide/_sg_variables.scss';
.-progress {
  position: absolute;
  width: 100%;
  height: 3px;
  z-index: 10;
  display: flex;
  align-items: baseline;
  top: 35px;
  /deep/ progress {
    margin: auto;
    height: 3px;
    -webkit-appearance: none;
    appearance: none;
    &::-webkit-progress-bar {
      background-color: rbga(255, 255, 255, 0.3);
    }
    &::-webkit-progress-value {
      background-color: $color-primary;
    }
  }
}
.-preparing {
  /deep/ .-play {
    display: none;
  }
}
.-asset {
  height: 100%;
}
</style>
